import regexpEscape from "regexp.escape";
function parseEdCommand(line: string, buffer: string[], dot: number, lastPattern: string): { range: EdRange, cmd: string, argument: string, lastPattern: string } | { errorMessage: string } {
  let addresses = [];
  let rest = line;
  let address = null;
  let separatorMode = ",";
  while (true) {
    try {
      [address, rest, lastPattern] = pullAddress(rest, buffer, dot, lastPattern);
    } catch(e) {
      if (e instanceof SyntaxError) {
        return { errorMessage: "invalid regular expression" };
      } else if (e.message == "invalid address") {
        return { errorMessage: "invalid address" };
      } else {
        throw e;
      }
    }
    if (address == -1) { return { errorMessage: "no match" }; }
    addresses.push(address);
    if (rest.length == 0) { break; }
    if (!",;".includes(rest[0])) { break; }
    if (rest[0] == ";") {
      dot = address ?? dot;
      separatorMode = ";";
    } else {
      separatorMode = ",";
    }
    rest = rest.slice(1);
  }
  let cmd = null;
  let cmdResult = pullCommand(rest);
  if (cmdResult == null) { return { errorMessage: "unknown command" }; }
  [cmd, rest] = cmdResult;
  let range = [null, null];
  addresses = addresses.slice(-2);
  if (cmd == "=") {
    let soleAddress = addresses.at(-1) ?? buffer.length;
    range = [soleAddress, soleAddress];
  } else {
    let defaultRange = separatorMode == "," ? [1, buffer.length] : [dot, buffer.length];
    if ("gw".includes(cmd) && addresses.length == 1 && addresses[0] == null) {
      range = [1, buffer.length];
    } else if (cmd == "\n" && addresses.length == 1 && addresses[0] == null) {
      range = [dot + 1, dot + 1];
    } else if (addresses.length == 1 && addresses[0] == null) {
      range = [dot, dot];
    } else if (addresses.length == 1) {
      range = [addresses[0], addresses[0]];
    } else if (addresses.length == 2) {
      if (addresses[0] == null && addresses[1] == null) {
        return { errorMessage: "invalid address" }
      } else if (addresses[0] == null) {
        return { errorMessage: "invalid address" };
      } else if (addresses[1] == null) {
        return { errorMessage: "invalid address" };
      } else {
        range = addresses;
      }
      if (cmd == "\n") { range = [range[1], range[1]]; }
    }
  }
  if (range[0] > buffer.length) { return { errorMessage: "invalid address" }; }
  if (range[1] > buffer.length) { return { errorMessage: "invalid address" }; }
  if (range.length != 2 || range[0] == null || range[1] == null) {
    console.warn(`Part of the range was null: ${JSON.stringify(range)}`);
  }
  let argument = rest;
  return { range, cmd, argument, lastPattern };
}

function pullCommand(line) {
  if (line.length == 0) {
    return ["\n", ""];
  } else if (/^[A-Za-z!=]$/.test(line[0])) {
    return [line[0], line.slice(1)];
  } else {
    return null;
  }
}

function pullAddress(line: string, buffer: string[], dot: number, lastPattern: string): [number, string, string] {
  const match = line.match(/^(\d+|\.|\$|[+\-]\d+|\/.*?(?<!\\)\/|\?.*?(?<!\\)\?)([\d+\-]*)([A-Za-z!=,;].*|)$/);
  if (match && match[1].length > 0) {
    if (match[1] == ".") {
      let addr = addExpr(dot, match[2]);
      if (addr < 0) { throw new Error("invalid address"); }
      return [addr, match[3], lastPattern];
    } else if (match[1] == "$") {
      let addr = addExpr(buffer.length, match[2]);
      if (addr < 0) { throw new Error("invalid address"); }
      return [addr, match[3], lastPattern];
    } else if (match[1][0] == "+" || match[1][0] == "-") {
      let addr = addExpr(dot, `${match[1]}${match[2]}`);
      if (addr < 0) { throw new Error("invalid address"); }
      return [addr, match[3], lastPattern];
    } else if (match[1].at(0) == "/" && match[1].at(-1) == "/") {
      let pattern = match[1].slice(1, -1);
      if (pattern == "" && lastPattern == "") { throw new Error("no previous pattern"); }
      if (pattern == "") { pattern = lastPattern; }
      let lineIdx = findMatchingLine(buffer, dot, pattern);
      return [addExpr(lineIdx, match[2]), match[3], pattern];
    } else if (match[1].at(0) == "?" && match[1].at(-1) == "?") {
      let pattern = match[1].slice(1, -1);
      if (pattern == "" && lastPattern == "") { throw new Error("no previous pattern"); }
      if (pattern == "") { pattern = lastPattern; }
      let lineIdx = findMatchingLineBackwards(buffer, dot, pattern);
      return [addExpr(lineIdx, match[2]), match[3], pattern];
    } else {
      return [addExpr(parseInt(match[1], 10), match[2]), match[3], lastPattern];
    }
  }
  return [null, line, lastPattern];
}

function findMatchingLine(buffer: string[], dot: number, pattern: string) {
  for (let i = dot; i < buffer.length; i++) {
    if (edReMatch(pattern, fromLine(buffer[i]))) {
      return i + 1;
    }
  }
  for (let i = 0; i < dot; i++) {
    if (edReMatch(pattern, fromLine(buffer[i]))) {
      return i + 1;
    }
  }
  return -1;
}

function findMatchingLineBackwards(buffer: string[], dot: number, pattern: string) {
  for (let i = dot - 2; i >= 0; i--) {
    if (edReMatch(pattern, fromLine(buffer[i]))) {
      return i + 1;
    }
  }
  for (let i = buffer.length - 1; i >= dot; i--) {
    if (edReMatch(pattern, fromLine(buffer[i]))) {
      return i + 1;
    }
  }
  return -1;
}

function addExpr(value: number, expr: string): number {
  const match = expr.match(/^([+\-])(\d+)(.*)/);
  if (!match) { return value; }
  if (match[1] == "+") {
    return addExpr(value + parseInt(match[2], 10), match[3]);
  } else if (match[1] == "-") {
    return addExpr(value - parseInt(match[2], 10), match[3]);
  }
  return value;
}

export function edReMatch(expression: string, string: string): boolean {
  expression = expression.replaceAll(/(?<!\\)([(){}\-|?+])/g, "\\$1");
  expression = expression.replaceAll(/\\([dDwWsStrnvfb0cxubB])/g, "$1");
  return new RegExp(expression).test(string);
}
function edReReplace(expression: string, replacement: string, line: EdLine): string {
  let string = fromLine(line);
  expression = expression.replaceAll(/(?<!\\)([(){}\-|?+])/g, "\\$1");
  expression = expression.replaceAll(/\\([dDwWsStrnvfb0cxubB])/g, "$1");
  replacement = replacement.replaceAll(/(?<!\\)([$])/g, "\$\$");
  replacement = replacement.replaceAll(/(?<!\\)&/g, "$$&");
  let newString = string.replace(new RegExp(expression), replacement).replaceAll("\\&", "&");
  return [line[0], newString];
}

type EdRange = [] | [number] | [number, number];
type EdCommand = (state: EdState, range: EdRange, argument: string) => [EdState, string[]];
type EdLineConsumer = (state: EdState) => [EdState, string[]];

type EdLine = [number, string];

export type EdState = {
  mode: "PROMPT" | "READ",
  afterRead: null | EdLineConsumer,
  lastError: string,
  dot: number,
  lastPattern: string,
  filename: string,
  files: Record<string, string>,
  buffer: EdLine[],
  readBuffer: string[] };

export const INITIAL_ED_STATE = Object.freeze({
  mode: "PROMPT",
  afterRead: null,
  lastError: "",
  lastPattern: "",
  filename: "",
  files: {},
  dot: 0,
  buffer: [],
  readBuffer: []
});

export function ed(state: EdState, line: string): [EdState, string[]] {
  if (state.mode == "PROMPT") {
    let { range, cmd, argument, lastPattern, errorMessage } = parseEdCommand(line, state.buffer, state.dot, state.lastPattern);
    if (errorMessage) { return error(state, errorMessage); }
    state = {...state, lastPattern };
    return runCommand(state, cmd, range, argument);
  } else if (state.mode == "READ") {
    if (line == ".") {
      return state.afterRead(state);
    }
    return appendReadBuffer(state, line);
  } else if (state.mode == "ESCAPED_MULTILINE") {
    if (line.at(-1) != "\\") {
      return state.afterRead({...state, readBuffer: [...state.readBuffer, line]});
    }
    return [{...state, readBuffer: [...state.readBuffer, line.slice(0, -1)]}, []];
  }
  return error(state, "unknown command");
}

function runCommand(state: EdState, cmd: string, range: EdRange, argument: string): [EdState, string[], string] {
  if (cmd == "a") {
    return append(state, range, argument);
  } else if (cmd == "i") {
    return insert(state, range, argument);
  } else if (cmd == "e") {
    return editFile(state, range, argument);
  } else if (cmd == "r") {
    return readFile(state, range, argument);
  } else if (cmd == "w") {
    return writeFile(state, range, argument);
  } else if (cmd == "p") {
    return print(state, range, argument);
  } else if (cmd == "l") {
    return literalPrint(state, range, argument);
  } else if (cmd == "c") {
    return change(state, range, argument);
  } else if (cmd == "d") {
    return deleet(state, range, argument);
  } else if (cmd == "s") {
    return substitute(state, range, argument);
  } else if (cmd == "g") {
    return globalCommand(state, range, argument);
  } else if (cmd == "=") {
    return lineNumber(state, range, argument);
  } else if (cmd == "\n") {
    return print(state, range, argument);
  } else {
    return error(state, "unknown command");
  }
}
function append(state: EdState, range: EdRange, argument: string): [EdState, string[]] {
  if (range.at(-1) > state.buffer.length) { return error(state, "invalid address"); }
  return [{...state, mode: "READ", afterRead: (state) => {
    let insertPoint = range.at(-1) ?? state.dot;
    let newBuffer = state.buffer.toSpliced(insertPoint, 0, ...toLines(state.readBuffer));
    return [{
      ...state,
      dot: insertPoint + state.readBuffer.length,
      afterRead: null,
      mode: "PROMPT",
      readBuffer: [],
      buffer: newBuffer},
    argument == "p" ? [fromLine(newBuffer[insertPoint + state.readBuffer.length - 1])] : []]
  }}, []];
}

function editFile(state: EdState, range: EdRange, argument: string): [EdState, string[]] {
  if (!Object.hasOwn(state.files, argument.trim())) {
    return [{...state, buffer: [], dot: 0, filename: argument.trim(), lastError: "cannot open input file" }, ["?"]];
  }
  let contents = state.files[argument.trim()];
  let lines = toLines(contents.split("\n"));
  if (lines.at(-1)[1] == "") { lines.pop(); }
  return [{ ...state, buffer: lines, dot: lines.length, filename: argument.trim() }, [`${contents.length}`]];
}

function writeFile(state: EdState, range: EdRange, argument: string): [EdState, string[]] {
  let filename = argument.trim().length == 0 ? state.filename : argument.trim();
  if (filename == "") { return error(state, "no current filename"); }
  let lines = state.buffer.slice(range[0] - 1, range[1]);
  let contents = fromLines(lines).join("\n") + "\n";
  let newFiles = {...state.files, [filename]: contents };
  return [{ ...state, files: newFiles, filename }, [`${contents.length}`]];
}

function readFile(state: EdState, range: EdRange, argument: string): [EdState, string[]] {
  let filename = argument.trim().length == 0 ? state.filename : argument.trim();
  if (filename == "") { return error(state, "no current filename"); }
  if (!Object.hasOwn(state.files, filename)) {
    return [{...state, filename, lastError: "cannot open input file" }, ["?"]];
  }
  let contents = state.files[filename];
  let lines = toLines(contents.split("\n"));
  if (lines.at(-1)[1] == "") { lines.pop(); }
  let newBuffer = [...state.buffer];
  let insertPoint = range[0];
  newBuffer.splice(insertPoint, 0, ...lines);
  return [{...state, buffer: newBuffer, dot: insertPoint + lines.length, filename }, [`${contents.length}`]];
}

function insert(state: EdState, range: EdRange, argument: string): [EdState, string[]] {
  if (range.at(-1) == 0) { range[range.length - 1] = 1; }
  if (range.at(-1) > state.buffer.length) { return error(state, "invalid address"); }
  return [{...state, mode: "READ", afterRead: (state) => {
    let insertPoint = range.at(-1) - 1;
    let newBuffer = state.buffer.toSpliced(insertPoint, 0, ...toLines(state.readBuffer));
    let newDot = state.readBuffer.length == 0 ? insertPoint + 1 : insertPoint + state.readBuffer.length;
    return [{
      ...state,
      dot: newDot,
      afterRead: null,
      mode: "PROMPT",
      readBuffer: [],
      buffer: newBuffer},
    argument == "p" ? [fromLine(newBuffer[newDot - 1])] : []];
  }}, []];
}

function print(state: EdState, range: EdRange, argument: string): [EdState, string[]] {
  if (range[0] == null) range[0] = 1;
  if (range[1] == null) range[1] = state.buffer.length;
  if (range[0] == 0) return error(state, "invalid address");
  if (range[0] > range[1]) return error(state, "invalid address");
  let outputLines = [];
  for (let i = range[0] - 1; i < range[1]; i++) {
    outputLines.push(state.buffer[i]);
  }
  return [{...state, dot: range[1]}, fromLines(outputLines)];
}

function literalPrint(state: EdState, range: EdRange, argument: string): [EdState, string[]] {
  if (range[0] == null) range[0] = 1;
  if (range[1] == null) range[1] = state.buffer.length;
  if (range[0] == 0) return error(state, "invalid address");
  if (range[0] > range[1]) return error(state, "invalid address");
  let outputLines = [];
  for (let i = range[0] - 1; i < range[1]; i++) {
    outputLines.push(...wrap(edEscape(fromLine(state.buffer[i]))));
  }
  return [
    {...state, dot: range[1]},
    [
      ...outputLines,
      ...(argument == "p" ? [fromLine(state.buffer[range[1] - 1])] : [])
    ]
  ];
}

function change(state: EdState, range: EdRange, argument: string): [EdState, string[]] {
  if (range.includes(0)) { return error(state, "invalid address"); }
  return [{...state, mode: "READ", afterRead: (state) => {
    let insertPoint = range[0] - 1;
    let deleteLength = range[1] - range[0] + 1;
    let newBuffer = state.buffer.toSpliced(insertPoint, deleteLength, ...toLines(state.readBuffer));
    return [{
      ...state,
      dot: insertPoint + state.readBuffer.length,
      afterRead: null,
      mode: "PROMPT",
      readBuffer: [],
      buffer: newBuffer},
    argument == "p" ? [fromLine(newBuffer[insertPoint + state.readBuffer.length - 1])] : []];
  }}, []];
}

function deleet(state: EdState, range: EdRange, argument: string): [EdState, string[]] {
  if (range.includes(0)) { return error(state, "invalid address"); }
  let deleteFrom = range[0] - 1;
  let deleteLength = range[1] - range[0] + 1;
  let newBuffer = state.buffer.toSpliced(deleteFrom, deleteLength);
  return [{
    ...state,
    dot: Math.min(newBuffer.length, deleteFrom + 1),
    buffer: newBuffer
  }, argument == "p" ? [fromLine(newBuffer[deleteFrom])] : []];
}

function substitute(state: EdState, range: EdRange, argument: string): [EdState, string[]] {
  let delim = argument[0];
  if (delim == " " || delim == "\\") { return error(state, "invalid pattern delimiter"); }
  if (argument.at(-1) == "\\") {
    return [{
      ...state,
      readBuffer: [argument.slice(0, -1)],
      mode: "ESCAPED_MULTILINE",
    afterRead: s => substitute({...s, mode: "PROMPT", afterRead: null, readBuffer: []}, range, s.readBuffer.join("\n")),
      }, []];
  }
  let [_pre, pattern, replacement, post] = argument
    .split(new RegExp(`(?<!\\\\)${regexpEscape(delim)}`))
    .map(s => s.replace(`\\${delim}`, delim));
  if (post != "" && post != "p") { return error(state, "invalid substitution command"); }
  if (pattern == "" && state.lastPattern == "") { return error(state, "no previous pattern"); }
  if (pattern == "") { pattern = state.lastPattern; }
  let newSlice = [];
  let lastReplacedLine = null;
  try {
    for (let i = range[0] - 1; i < range[1]; i++) {
      if (edReMatch(pattern, fromLine(state.buffer[i]))) {
        let replaced = edReReplace(pattern, replacement, state.buffer[i]);
        newSlice.push(...toLines(fromLine(replaced).split("\n")));
        lastReplacedLine = range[0] + newSlice.length - 1;
      } else {
        newSlice.push(state.buffer[i]);
      }
    }
  } catch(e) {
    if (e instanceof SyntaxError) {
      return error(state, "invalid regular expression");
    } else {
      throw e;
    }
  }
  state = {...state, lastPattern: pattern};
  if (lastReplacedLine === null) {
    return error(state, "no match");
  }
  let newBuffer = state.buffer.toSpliced(range[0] - 1, range[1] - range[0] + 1, ...newSlice);
  return [{
    ...state,
    dot: lastReplacedLine,
    buffer: newBuffer
  }, post == "p" ? [fromLine(newBuffer[lastReplacedLine - 1])] : []];
}

function lineNumber(state: EdState, range: EdRange, argument: string): [EdState, string[]] {
  let address = range.at(-1);
  return [state, [
    `${address}`,
    ...(argument == "p" ? [fromLine(state.buffer[state.dot - 1])] : [])
  ]];
}

function globalCommand(state: EdState, range: EdRange, argument: string): [EdState, string[]] {
  if (argument.at(-1) == "\\") {
    return [{
      ...state,
      readBuffer: [argument.slice(0, -1)],
      mode: "ESCAPED_MULTILINE",
      afterRead: s => globalCommand({...s, mode: "PROMPT", afterRead: null, readBuffer: []}, range, s.readBuffer.join("\n")),
      }, []];
  }
  let delim = argument[0];
  if (delim == " " || delim == "\\") { return error(state, "invalid pattern delimiter"); }
  let [_pre, matchExpression] = argument
    .split(new RegExp(`(?<!\\\\)${regexpEscape(delim)}`))
    .map(s => s.replace(`\\${delim}`, delim));
  let command = argument.replace(new RegExp(`.*?(?<!\\\\)${regexpEscape(delim)}.*?(?<!\\\\)${regexpEscape(delim)}`), "");
  if (matchExpression == "" && state.lastPattern == "") { return error(state, "no previous pattern"); }
  if (matchExpression == "") { matchExpression = state.lastPattern; }
  let output = [];
  try {
    for (let i = range[0] - 1; i < range[1]; i++) {
      if (edReMatch(matchExpression, fromLine(state.buffer[i]))) {
        state.buffer[i][0] = 1;
      }
    }
  } catch(e) {
    if (e instanceof SyntaxError) {
      return error(state, "invalid regular expression");
    } else {
      throw e;
    }
  }
  state = {...state, lastPattern: matchExpression };
  let i = 0;
  while (i < state.buffer.length) {
    if (state.buffer[i][0] == 1) {
      if (command.trim() == "") { command = "p"; }
      let { range: subRange, cmd: subCmd, argument: subArgument, errorMessage: subErrorMessage } =
        parseEdCommand(command, state.buffer, i + 1);
      if (subErrorMessage) { return error(state, subErrorMessage); }
      if ("aicwre".includes(subCmd)) { return error(state, "invalid global command"); }
      if (subCmd == "g") { return error(state, "cannot nest global commands"); }
      if (subCmd.trim() == "") { subCmd = "p"; }
      let result = runCommand({...state, dot: i + 1, lastError: ""}, subCmd, subRange, subArgument);
      output.push(...result[1]);
      state = result[0];
      if (state.lastError != "") { return [state, output]; }
      i = subCmd == "d" ? i : i + 1
    } else {
      i = i + 1;
    }
  }
  for (let i = 0; i < state.buffer.length; i++) {
    state.buffer[i][0] = 0;
  }
  return [state, output];
}

function appendReadBuffer(state: EdState, line: string): [EdState, string[]] {
  return [{...state, readBuffer: [...state.readBuffer, line]}, []];
}

export function loadNewFile(state: EdState, name: string, content: string): EdState {
  return {
    ...state,
    files: {
      ...state.files,
      [name]: content
    }
  }
}

function error(state: EdState, message: string) {
  return [{...state, lastError: message}, ["?"]];
}

function edEscape(string: string) {
  return (string
    .replaceAll(/\x08/g, "-\b\\")
    .replaceAll(/\t/g, "-\b>")
    .replaceAll(/\r/g, "-\b<")
    .replaceAll(/\x0F/g, "I\b-")
    .replaceAll(/\x0E/g, "O\b-")
    .replaceAll(/\x1B/g, "^\b")
    .replaceAll(/\x07/g, "=\b*"));
}

function wrap(string: string, length: number = 72) {
  return string.replaceAll(/.{72}/g, "$&\\\n").split("\n");
}

export function toLines(lines: string[]): EdLine[] {
  return lines.map(l => [0, l]);
}

function fromLines(lines: EdLine[]): string[] {
  return lines.map(l => l[1]);
}

function fromLine(line: EdLine) {
  return line[1];
}
